// Theme selection.
//
// Light is the default. The choice is persisted per browser and applied as a
// `data-theme` attribute on <html>, which is also set by an inline script in
// index.html before first paint so there is no flash of the wrong theme.
//
// Embeds can be pinned with `?theme=dark`, since the host page — not the
// visitor's Cloudish preference — decides what looks right in an iframe.
//
// A page can also lay a temporary theme over the preference, never stored:
// the homepage goes dark while its intro plays (IntroAmbience.tsx), and
// clearing the override brings the preference back. Choosing a theme while
// one is on wins over it until it's cleared, so the toggle does what it says.

import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE = 'cloudish.theme';
export const DEFAULT_THEME: ThemePreference = 'light';

const listeners = new Set<(preference: ThemePreference) => void>();
let override: ResolvedTheme | null = null;
let overrideDismissed = false;

function systemTheme(): ResolvedTheme {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? systemTheme() : preference;
}

export function readPreference(): ThemePreference {
  // Prerendering (see entry-server.tsx) calls this from Node, where
  // `window`/`localStorage` don't exist — always the light default there,
  // same as a real first-time browser visitor with nothing stored yet.
  if (typeof window === 'undefined') return DEFAULT_THEME;

  // A pinned embed theme wins over anything stored.
  const pinned = new URLSearchParams(window.location.search).get('theme');
  if (pinned === 'light' || pinned === 'dark') return pinned;

  const stored = localStorage.getItem(THEME_STORAGE);
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : DEFAULT_THEME;
}

export function applyTheme(preference: ThemePreference) {
  const resolved = override ?? resolveTheme(preference);
  document.documentElement.dataset.theme = resolved;
  // Lets the browser style form controls, scrollbars and caret to match.
  document.documentElement.style.colorScheme = resolved;
}

export function setPreference(preference: ThemePreference) {
  if (preference === DEFAULT_THEME) localStorage.removeItem(THEME_STORAGE);
  else localStorage.setItem(THEME_STORAGE, preference);
  if (override) {
    override = null;
    overrideDismissed = true;
  }
  applyTheme(preference);
  for (const listener of listeners) listener(preference);
}

/** Lays `theme` over the preference until called with null (see the header
 *  comment). Ignored while a choice made during the last override stands. */
export function setThemeOverride(theme: ResolvedTheme | null) {
  if (theme === null) overrideDismissed = false;
  else if (overrideDismissed) return;
  if (override === theme) return;
  override = theme;
  const preference = readPreference();
  applyTheme(preference);
  for (const listener of listeners) listener(preference);
}

/**
 * Subscribes a component to the current theme.
 * Returns the stored preference, what it resolves to, and setters.
 */
export function useTheme() {
  // The override is part of the state, so a component re-renders when only
  // the override changes and the stored preference doesn't.
  const [state, setState] = useState(() => ({ preference: readPreference(), override }));
  const { preference } = state;

  useEffect(() => {
    const listener = (next: ThemePreference) => setState({ preference: next, override });
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  // Follow the OS while the preference is "system".
  useEffect(() => {
    if (preference !== 'system') return undefined;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [preference]);

  const set = useCallback((next: ThemePreference) => setPreference(next), []);
  const toggle = useCallback(() => {
    setPreference((override ?? resolveTheme(readPreference())) === 'dark' ? 'light' : 'dark');
  }, []);

  return { preference, resolved: state.override ?? resolveTheme(preference), setTheme: set, toggle };
}

/** Applies the stored preference at startup. */
export function initTheme() {
  applyTheme(readPreference());
}
